import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  memo,
  useRef,
  useState,
} from "react";
import { keyframes } from "@emotion/react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Skeleton } from "@UI/Components/Skeleton/Skeleton";
import { Text } from "@UI/Components/Text/Text";
import { Textarea } from "@UI/Components/Textarea/Textarea";
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
} from "@UI/Components/Form/Form";
import { Button } from "@UI/Components/Button/Button";
import { IconButton } from "@UI/Components/Button/IconButton";
import { Icon } from "@UI/Components/Icon/Icon";
import { CircleInfo } from "@UI/Components/Icons/CircleInfo";
import { ImageCirclePlus } from "@UI/Components/Icons/ImageCirclePlus";
import { Microphone } from "@UI/Components/Icons/Microphone";
import { SparklesSolid } from "@UI/Components/Icons/SparklesSolid";
import { Stop } from "@UI/Components/Icons/Stop";
import { Xmark } from "@UI/Components/Icons/Xmark";
import useThemeToken from "@UI/hooks/useThemeToken";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";

import { useTypewriterPlaceholder } from "../../hooks/useTypewriterPlaceholder";
import messages from "../../messages";
import { RetryingImage } from "../RetryingImage/RetryingImage";

/** One field per screen, so a module-level id cannot collide with itself. */
const SPARKLE_GRADIENT_ID = "rtDescriptionSparkleGradient";

/**
 * Figma's thumbnail strip: 32px squares on mobile, 50px from `lg` up. The
 * remove badge and its offset are sized off the same scale so the × sits on
 * the corner at both sizes rather than swallowing the small one.
 */
const THUMBNAIL_SIZE = { base: "32px", lg: "50px" };
const REMOVE_BADGE_SIZE = { base: "16px", lg: "20px" };
const REMOVE_BADGE_OFFSET = { base: "-6px", lg: "-8px" };

/** Recording dot: breathes while listening, static red under reduced motion. */
const recordingPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const RECORDING_DOT_SX = {
  animation: `${recordingPulse} 1.2s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

/**
 * One photo already uploaded and showing in the strip.
 *
 * `url` is the browser's own `blob:` preview of the picked file, not the
 * server's `thumb_url` — that one points at a processed variant a background
 * job has not generated yet, so it 404s for a while after the upload succeeds.
 * `PhotoUploader` shows the same thing for the same reason.
 */
export interface IDescriptionPhoto {
  id: number;
  url: string;
}

export interface IDescriptionFieldProps {
  value: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur: FocusEventHandler<HTMLTextAreaElement>;
  name: string;
  placeholders: string[];
  error?: string;
  isSubmitting: boolean;
  maxLength: number;
  /**
   * Dictation, owned by the container (RT-4340). Defaulted off so a caller that
   * knows nothing about speech — Storybook, the field on its own — renders the
   * pre-dictation toolbar rather than a mic that cannot work.
   */
  isDictationSupported?: boolean;
  isListening?: boolean;
  onToggleDictation?: () => void;
  /**
   * Photos, owned by the container the same way dictation is. Defaulted to an
   * empty strip and a button that opens the picker but reports nothing, so a
   * caller that knows nothing about uploads still renders a sane toolbar.
   */
  photos?: IDescriptionPhoto[];
  /** Uploads still in flight — one placeholder square each, after the strip. */
  pendingPhotoCount?: number;
  /** The id whose DELETE is in flight; its square stays put but goes quiet. */
  removingPhotoId?: number | null;
  /** `constraints.allowed_extensions`, lowercase and without the dot. */
  allowedPhotoExtensions?: string[];
  /** False once the limit is reached: the button stays live but reports
   * instead of opening a picker whose files would all be dropped. */
  canAddPhotos?: boolean;
  onPhotosSelected?: (files: FileList | null) => void;
  /** The button was pressed with no slot left to fill. */
  onAddPhotosBlocked?: () => void;
  onRemovePhoto?: (id: number) => void;
  /** Edit mode: plain gray border, no sparkle, no photos. */
  isEditing?: boolean;
}

/**
 * Presentational description field for the task creation flow (RT-4321 M2).
 *
 * A composite: a gradient-bordered box wrapping the textarea and a toolbar row
 * (mic left, counter + photos right). No network, no form library —
 * `onChange`/`onBlur`/`name` and the forwarded ref are shaped so a react-hook-form
 * `register("description")` spread works directly.
 */
export const DescriptionField = memo(
  forwardRef<HTMLTextAreaElement, IDescriptionFieldProps>(
    (
      {
        value,
        onChange,
        onBlur,
        name,
        placeholders,
        error,
        isSubmitting,
        maxLength,
        isDictationSupported = false,
        isListening = false,
        onToggleDictation,
        photos = [],
        pendingPhotoCount = 0,
        removingPhotoId = null,
        allowedPhotoExtensions = [],
        canAddPhotos = true,
        onPhotosSelected,
        onAddPhotosBlocked,
        onRemovePhoto,
        isEditing = false,
      },
      ref
    ) => {
      const { formatMessage } = useI18n();
      const [isFocused, setIsFocused] = useState(false);
      const fileInputRef = useRef<HTMLInputElement>(null);

      // Destructured: `useThemeToken` returns string[], and `stopColor` needs a
      // scalar. The single-subkey form only worked by array stringification.
      const [pink500, orange500] = useThemeToken("colors", [
        "pink.500",
        "orange.500",
      ]);
      const borderPaint = `linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%)`;

      const isTypewriterEnabled = !isFocused && value === "" && !isSubmitting;
      const animatedPlaceholder = useTypewriterPlaceholder(
        placeholders,
        isTypewriterEnabled
      );

      // Focused means no placeholder at all — the field is the user's now. It
      // also means nothing is left for a screen reader to announce on focus,
      // which a half-typed animated string otherwise would be.
      const placeholder = isFocused ? "" : animatedPlaceholder;

      const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (event) => {
        setIsFocused(false);
        onBlur(event);
      };

      const hasText = value.length > 0;
      const isOverLimit = value.length > maxLength;

      return (
        <FormControl isInvalid={!!error}>
          <Box
            padding={isEditing ? "1px" : "2px"}
            borderRadius={isEditing ? "11px" : "12px"}
            background={isEditing ? "gray.400" : borderPaint}
          >
            <Box background="white" borderRadius="10px" padding="12px">
              <Flex>
                {/* Figma puts a sparkle left of the text, top-aligned: the
                    assistant reads what is written here. Decorative only.
                    It carries the same gradient as the border, which an SVG
                    glyph can only take from a paint server — `bgClip: text`
                    works on text, not on a path's fill. */}
                {!isEditing && (
                  <>
                  <svg
                    width="0"
                    height="0"
                    aria-hidden
                    focusable="false"
                    style={{ position: "absolute", width: 0, height: 0 }}
                  >
                    <defs>
                      {/* Figma's stop is at 94.156deg. objectBoundingBox gradients
                          take fractional x/y rather than an angle, so this is an
                          approximation of that angle (near-vertical, slightly
                          tilted) rather than an exact conversion. */}
                      <linearGradient
                        id={SPARKLE_GRADIENT_ID}
                        x1="45%"
                        y1="0%"
                        x2="55%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor={pink500} />
                        <stop offset="99.871%" stopColor={orange500} />
                      </linearGradient>
                    </defs>
                  </svg>
                  <Icon
                    as={SparklesSolid}
                    width="16px"
                    height="16px"
                    marginTop="4px"
                    marginRight="8px"
                    flexShrink={0}
                    aria-hidden
                    sx={{ path: { fill: `url(#${SPARKLE_GRADIENT_ID})` } }}
                  />
                  </>
                )}
                <Textarea
                  ref={ref}
                  name={name}
                  value={value}
                  onChange={onChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={handleBlur}
                  variant="unstyled"
                  resize="none"
                  minHeight="92px"
                  padding="0"
                  fontSize="md"
                  isReadOnly={isSubmitting}
                  placeholder={placeholder}
                  aria-label={formatMessage(
                    messages["taskCreation.description.label"]
                  )}
                  _placeholder={{ color: "gray.400", fontStyle: "italic" }}
                />
              </Flex>
              {/* Figma's strip: the photos sit between the text and the
                  toolbar, above the mic. Squares only — the caption field is
                  the photos screen's job, and this one is an entry screen. */}
              {!isEditing && (photos.length > 0 || pendingPhotoCount > 0) && (
                <Flex
                  gap="8px"
                  flexWrap="wrap"
                  marginTop="8px"
                  marginBottom="12px"
                >
                  {photos.map((photo) => {
                    const isRemoving = removingPhotoId === photo.id;

                    return (
                      <Box
                        key={photo.id}
                        position="relative"
                        flexShrink={0}
                        opacity={isRemoving ? 0.5 : 1}
                      >
                        <RetryingImage
                          src={photo.url}
                          alt={formatMessage(
                            messages["taskCreation.photos.thumbnailAlt"]
                          )}
                          boxSize={THUMBNAIL_SIZE}
                          objectFit="cover"
                          borderRadius="8px"
                        />
                        <Box
                          as="button"
                          type="button"
                          aria-label={formatMessage(
                            messages["taskCreation.photos.removeButton"]
                          )}
                          onClick={() => onRemovePhoto?.(photo.id)}
                          // Every ×, not just this photo's: the uploader
                          // tracks one removal at a time, so a second DELETE
                          // started while the first is in flight would clear
                          // the busy flag as soon as the *first* one lands —
                          // and the CTA would unlock over a photo the server
                          // is still deleting.
                          disabled={isSubmitting || removingPhotoId != null}
                          position="absolute"
                          top={REMOVE_BADGE_OFFSET}
                          right={REMOVE_BADGE_OFFSET}
                          boxSize={REMOVE_BADGE_SIZE}
                          borderRadius="full"
                          background="white"
                          borderWidth="1px"
                          borderColor="gray.200"
                          boxShadow="sm"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          cursor="pointer"
                          transition="transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease"
                          _hover={{ transform: "scale(1.1)", background: "gray.100", borderColor: "gray.300" }}
                        >
                          <Icon
                            as={Xmark}
                            width={{ base: "8px", lg: "10px" }}
                            height={{ base: "8px", lg: "10px" }}
                            color="gray.900"
                          />
                        </Box>
                      </Box>
                    );
                  })}
                  {/* Keyed by index on purpose: these are placeholders for
                      files that have no id until their upload lands, and the
                      list only ever shrinks from the front as they do. */}
                  {Array.from({ length: pendingPhotoCount }).map(
                    (_, index) => (
                      <Skeleton
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        boxSize={THUMBNAIL_SIZE}
                        borderRadius="8px"
                        flexShrink={0}
                      />
                    )
                  )}
                </Flex>
              )}
              {!isEditing && (
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  accept={allowedPhotoExtensions
                    .map((extension) => `.${extension}`)
                    .join(",")}
                  onChange={(event) => {
                    onPhotosSelected?.(event.target.files);
                    // Cleared so picking the same file twice in a row still
                    // fires a change event.
                    event.target.value = "";
                  }}
                />
              )}
              <Flex height="32px" alignItems="center" justifyContent="space-between">
                {/* Figma's mic chip: a square icon button at rest, widening
                    into a labelled pill while the browser listens. Rendered
                    only when dictation can actually work — an unsupported
                    browser (Firefox, or a Chromium build whose speech service
                    refused at runtime) gets no mic at all rather than one that
                    fails on click. */}
                {isDictationSupported ? (
                  isListening ? (
                    // A plain `Button`, not `IconButton`: Chakra's `IconButton`
                    // hardcodes `padding: 0` internally, which always wins over
                    // a `paddingX` prop regardless of prop order — the 12px
                    // gutter this pill needs never renders through it.
                    <Button
                      onClick={onToggleDictation}
                      aria-label={formatMessage(
                        messages["taskCreation.dictation.stop"]
                      )}
                      aria-pressed
                      variant="outline"
                      height="32px"
                      paddingX="12px"
                      borderRadius="10px"
                      borderColor="red.500"
                      background="white"
                      leftIcon={
                        // The pulsing dot is the only "we are recording" signal
                        // that survives a glance; the word carries it for
                        // everyone else.
                        <Box
                          width="12px"
                          height="12px"
                          borderRadius="full"
                          background="red.500"
                          sx={RECORDING_DOT_SX}
                        />
                      }
                    >
                      <Text fontSize="xs" color="gray.800">
                        <FormattedMessage
                          {...messages["taskCreation.dictation.listening"]}
                        />
                      </Text>
                    </Button>
                  ) : (
                    <IconButton
                      onClick={onToggleDictation}
                      aria-label={formatMessage(
                        messages["taskCreation.dictation.start"]
                      )}
                      aria-pressed={false}
                      variant="outline"
                      height="32px"
                      width="40px"
                      minWidth="40px"
                      borderRadius="10px"
                      borderColor="gray.300"
                      background="white"
                      icon={
                        <Icon
                          as={Microphone}
                          width="16px"
                          height="16px"
                          color="gray.800"
                        />
                      }
                    />
                  )
                ) : (
                  // Nothing on the left, but the counter and the photo button
                  // stay pinned right — `space-between` needs both ends.
                  <Box />
                )}
                <Flex alignItems="center" gap="8px">
                  {hasText && (
                    <Text
                      fontSize="xs"
                      color={isOverLimit ? "red.500" : "gray.500"}
                    >
                      {/* Digits and a slash — nothing to translate. */}
                      {`${value.length}/${maxLength}`}
                    </Text>
                  )}
                  {/* Figma swaps the photo button for Stop while listening —
                      the same slot, so the toolbar never grows a third
                      control. Both stop affordances (this and the pill) fire
                      the same toggle. */}
                  {isListening ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onToggleDictation}
                      leftIcon={<Icon as={Stop} width="4" height="4" />}
                    >
                      <FormattedMessage
                        {...messages["taskCreation.dictation.stop"]}
                      />
                    </Button>
                  ) : (
                    !isEditing && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        isDisabled={isSubmitting}
                        // Live at the limit too: a disabled button cannot say
                        // why it is disabled, and the limit is the one refusal
                        // the user can act on (remove a photo, then retry).
                        onClick={() =>
                          canAddPhotos
                            ? fileInputRef.current?.click()
                            : onAddPhotosBlocked?.()
                        }
                        leftIcon={
                          <Icon as={ImageCirclePlus} width="4" height="4" />
                        }
                      >
                        <FormattedMessage
                          {...messages["taskCreation.photos.addButton"]}
                        />
                      </Button>
                    )
                  )}
                </Flex>
              </Flex>
            </Box>
          </Box>
          {error && (
            <Flex alignItems="center" gap="6px" marginTop="8px">
              <Icon as={CircleInfo} width="16px" height="16px" color="red.700" aria-hidden />
              <FormErrorMessage margin="0" color="red.700" fontSize="sm">
                {error}
              </FormErrorMessage>
            </Flex>
          )}
          <FormHelperText fontSize="xs" color="gray.500">
            <FormattedMessage
              {...messages["taskCreation.description.publicNotice"]}
            />
          </FormHelperText>
        </FormControl>
      );
    }
  )
);

DescriptionField.displayName = "DescriptionField";
